import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Calls from './pages/Calls';
import CallDetail from './pages/CallDetail';
import Leads from './pages/Leads';
import Analytics from './pages/Analytics';
import InsightConversations from './pages/InsightConversations';
import Settings from './pages/Settings';
import Flows from './pages/Flows';
import FlowBuilder from './pages/FlowBuilder.jsx';
import Deployments from './pages/Deployments';
import Operations from './pages/Operations';
import Train from './pages/Train';
import CustomData from './pages/CustomData';
import DefaultData from './pages/DefaultData.jsx';
import ApiManagement from './pages/ApiManagement';
import FunctionManagement from './pages/FunctionManagement';
import Localization from './pages/Localization';
import Channels from './pages/Channels';
import Inbox from './pages/Inbox';
import AgentDesk from './pages/AgentDesk';
import SupervisorMonitor from './pages/SupervisorMonitor';
import Login from './pages/Login';
import Pricing from './pages/Pricing';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import BotSelector from './pages/BotSelector';
import AdminDashboard from './pages/AdminDashboard';
import PlatformControl from './pages/PlatformControl';
import PlatformAuditLogs from './pages/PlatformAuditLogs.jsx';
import CampaignHub from './pages/CampaignHub';
import Customer360 from './pages/Customer360';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeModeContext';

/** Purpose: gate private routes on a valid session. Why: prevents unauthenticated users from reaching workspace screens. */
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  // Show loading while checking session
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/** Purpose: restrict platform administration pages to administrators. Why: route-level checks keep privileged screens out of ordinary user navigation. */
const AdminOnlyRoute = ({ children, fallback = '/analytics' }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin()) {
    return <Navigate to={fallback} replace />;
  }

  return children;
};

/** Purpose: choose the user's landing page from their role. Why: one home route can safely serve both admins and workspace users. */
const RoleHomeRedirect = () => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return <Navigate to={isAdmin() ? '/admin' : '/analytics'} replace />;
};

/** Purpose: enforce service entitlements before rendering a feature route. Why: hidden navigation alone is not an access-control boundary. */
const ServiceRoute = ({ service, anyOf, fallback = '/analytics', children }) => {
  const { isAdmin, hasServiceAccess, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const isAllowed = Array.isArray(anyOf) && anyOf.length > 0
    ? anyOf.some((s) => hasServiceAccess(s))
    : hasServiceAccess(service);

  if (!isAdmin() && !isAllowed) {
    return <Navigate to={fallback} replace />;
  }

  return children;
};

/** Purpose: compose route content with shared providers/layout. Why: routes need one consistent auth, theme, and navigation boundary. */
function AppShell() {
  const { mode } = useThemeMode();

  /** Purpose: Apply the selected light or dark palette to the application tree. Why: One theme provider keeps page and shared component styling consistent. */
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#6395ff' : '#2563eb',
        dark: mode === 'dark' ? '#3975ed' : '#1d4ed8',
        light: mode === 'dark' ? '#9bbcff' : '#60a5fa',
        contrastText: '#ffffff',
      },
      secondary: {
        main: mode === 'dark' ? '#a78bfa' : '#0f766e',
        light: mode === 'dark' ? '#c4b5fd' : '#14b8a6',
      },
      background: {
        default: mode === 'dark' ? '#101216' : '#f5f7fb',
        paper: mode === 'dark' ? '#181b21' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#edf0f5' : '#0f172a',
        secondary: mode === 'dark' ? '#a6afbe' : '#475569',
      },
      ...(mode === 'dark' ? {
        divider: '#2c323d',
        action: {
          hover: 'rgba(148,163,184,0.08)',
          selected: 'rgba(99,149,255,0.12)',
          disabled: '#737d8d',
          disabledBackground: 'rgba(148,163,184,0.10)',
          focus: 'rgba(99,149,255,0.16)',
        },
      } : {}),
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h5: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 700,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: mode === 'dark'
              ? '#101216'
              : '#f5f7fb',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: mode === 'dark' ? '1px solid rgba(148,163,184,0.12)' : '1px solid rgba(148,163,184,0.16)',
            ...(mode === 'dark' ? { backgroundImage: 'none' } : {}),
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'dark'
              ? '0 2px 8px rgba(2,6,23,0.20)'
              : '0 1px 3px rgba(15,23,42,0.04)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          ...(mode === 'dark' ? {
            containedPrimary: { backgroundColor: '#316de0', color: '#fff', '&:hover': { backgroundColor: '#407beb' } },
            outlined: { borderColor: '#3a4454' },
          } : {}),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            ...(mode === 'dark' ? {
              backgroundColor: '#13161c',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#39414e' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#647087' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6395ff' },
            } : {}),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.18)',
          },
          head: {
            backgroundColor: mode === 'dark' ? '#1e222a' : '#f8fafc',
            color: mode === 'dark' ? '#cbd5e1' : '#475569',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(99,149,255,0.06)' : 'rgba(37,99,235,0.035)',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 16 },
        },
      },
    },
  }), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ResetPassword />} />
            
            {/* Protected routes with layout */}
            <Route
              path="/bots"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <BotSelector />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <Navigate to="/platform-control?section=audit" replace />
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RoleHomeRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Navigate to="/analytics" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calls"
              element={
                <ProtectedRoute>
                  <ServiceRoute service="voice">
                    <Layout>
                      <Calls />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calls/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CallDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Leads />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/conversations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InsightConversations />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flows"
              element={
                <ProtectedRoute>
                  <ServiceRoute anyOf={['voice', 'chat', 'whatsapp']}>
                    <Layout>
                      <Flows />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flows/new"
              element={
                <ProtectedRoute>
                  <ServiceRoute anyOf={['voice', 'chat', 'whatsapp']}>
                    <Layout>
                      <FlowBuilder />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flows/:flowId"
              element={
                <ProtectedRoute>
                  <ServiceRoute anyOf={['voice', 'chat', 'whatsapp']}>
                    <Layout>
                      <FlowBuilder />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/channels"
              element={
                <ProtectedRoute>
                  <ServiceRoute anyOf={['voice', 'chat', 'whatsapp']}>
                    <Layout>
                      <Channels />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/campaign-hub" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/templates" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/templates/new" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/templates/:templateId" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/campaigns" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/notifications" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Navigate to="/data/default?table=notifications" replace /></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/contacts" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/audiences" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/campaign-hub/analytics" element={<ProtectedRoute><ServiceRoute service="whatsapp"><Layout><CampaignHub /></Layout></ServiceRoute></ProtectedRoute>} />
            <Route path="/customer-360" element={<ProtectedRoute><Layout><Customer360 /></Layout></ProtectedRoute>} />
            <Route path="/customer-360/customers" element={<ProtectedRoute><Layout><Customer360 /></Layout></ProtectedRoute>} />
            <Route path="/customer-360/customers/:id" element={<ProtectedRoute><Layout><Customer360 /></Layout></ProtectedRoute>} />
            <Route path="/customer-360/consent" element={<ProtectedRoute><Layout><Customer360 /></Layout></ProtectedRoute>} />
            <Route path="/customer-360/timeline" element={<ProtectedRoute><Layout><Customer360 /></Layout></ProtectedRoute>} />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Inbox />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox/:section"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Inbox />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/agent-desk/:section" element={<ProtectedRoute><Layout><AgentDesk /></Layout></ProtectedRoute>} />
            <Route path="/agent-desk/monitor/live" element={<Navigate to="/agent-desk/operations" replace />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-management"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApiManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/apis"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApiManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/localization"
              element={<ProtectedRoute><Layout><Localization /></Layout></ProtectedRoute>}
            />
            <Route
              path="/function-management"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FunctionManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/functions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FunctionManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/deployments"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Deployments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <Layout><Operations /></Layout>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/train"
              element={
                <ProtectedRoute>
                  <ServiceRoute anyOf={['chat', 'whatsapp']}>
                    <Layout>
                      <Train />
                    </Layout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data"
              element={
                <ProtectedRoute>
                  <Navigate to="/data/default" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data/default"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DefaultData />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data/custom"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomData />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-control"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute>
                    <Layout>
                      <PlatformControl />
                    </Layout>
                  </AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
      </ThemeProvider>
  );
}

/** Purpose: initialize the application and its top-level context providers. Why: routing and workspace state must be available throughout the UI. */
/** Purpose: mount the application-wide providers and router. Why: every page must share the same authentication, theme, and route context. */
function App() {
  return (
    <ThemeModeProvider>
      <AppShell />
    </ThemeModeProvider>
  );
}

export default App;
